import React, { useState, useEffect } from 'react';
import { Card, Button, ListGroup, Spinner } from 'react-bootstrap';
import api from '../api/api';
import { useToast } from './Toasts';

function StreamingSidebar({ darkMode, cardBgClass }) {
  const { notify } = useToast();
  const initialPlatforms = {
    Twitch: null,
    YouTube: null,
    Kick: null
  };

  const [platformStatus, setPlatformStatus] = useState(initialPlatforms);
  const [containerStatus, setContainerStatus] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchContainerStatus = async () => {
    try {
      const res = await api.get('/container-status');
      setContainerStatus(res.data.status);
    } catch (err) {
      console.error('Fehler beim Abrufen des Container-Status:', err);
    }
  };

  const handleContainerAction = async (action) => {
    setActionLoading(action);
    try {
      await api.post('/container-action', { action });
      setTimeout(fetchContainerStatus, 2500);
    } catch (err) {
      notify('Aktion fehlgeschlagen: ' + (err.response?.data?.error || err.message), 'danger');
    }
    setActionLoading(false);
  };

  const fetchStatus = async () => {
    try {
      const res = await api.get('/stream-status');
      setPlatformStatus((prev) => ({
        ...prev,
        ...res.data
      }));
    } catch (err) {
      console.error('Fehler beim Abrufen des Stream-Status:', err);
    }
  };

  const getStatusIcon = (status) => {
    if (status === true) return '✅';
    if (status === false) return '❌';
    return '❓';
  };

  const getIconSrc = (name) => {
    return `/icons/${name.toLowerCase()}.svg`;
  };

  useEffect(() => {
    fetchStatus();
    fetchContainerStatus();
    const interval = setInterval(() => {
      fetchStatus();
      fetchContainerStatus();
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className={`p-3 shadow-sm ${darkMode ? cardBgClass : 'bg-white text-dark'}`}>
      <h5 className="mb-3">🎥 Streaming-Plattformen</h5>
      <ListGroup variant={darkMode ? 'dark' : undefined} className="mb-3">
        {Object.entries(platformStatus).map(([name, info]) => {
          const status = info?.live;
          const url = info?.url;
          const iconSrc = getIconSrc(name);

          return (
            <ListGroup.Item key={name} className={`d-flex align-items-center gap-2 ${darkMode ? cardBgClass : ''}`}>
              <span>{getStatusIcon(status)}</span>
              <img
                src={iconSrc}
                alt={`${name} Icon`}
                width="20"
                height="20"
                style={{ opacity: 0.8 }}
              />
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: darkMode ? '#fff' : '#000' }}
                >
                  {name}
                </a>
              ) : (
                name
              )}
            </ListGroup.Item>
          );
        })}
      </ListGroup>

      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center gap-2 mb-1">
          <small className={darkMode ? 'text-secondary' : 'text-muted'}>
            Container:{' '}
            <strong>
              {containerStatus === 'running' && '🟢 Läuft'}
              {containerStatus === 'exited' && '🔴 Gestoppt'}
              {containerStatus === 'not_found' && '⚫ Nicht gefunden'}
              {containerStatus === null && '⏳ Laden...'}
              {containerStatus !== null && !['running', 'exited', 'not_found'].includes(containerStatus) && `⚠️ ${containerStatus}`}
            </strong>
          </small>
        </div>

        {containerStatus === 'running' ? (
          <div className="d-flex gap-2">
            <Button
              variant="warning"
              size="sm"
              className="flex-fill"
              disabled={!!actionLoading}
              onClick={() => handleContainerAction('restart')}
            >
              {actionLoading === 'restart' ? (
                <><Spinner size="sm" animation="border" className="me-1" />Neustart...</>
              ) : '🔄 Neu starten'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="flex-fill"
              disabled={!!actionLoading}
              onClick={() => handleContainerAction('stop')}
            >
              {actionLoading === 'stop' ? (
                <><Spinner size="sm" animation="border" className="me-1" />Stoppe...</>
              ) : '⏹ Stoppen'}
            </Button>
          </div>
        ) : (
          <Button
            variant="success"
            size="sm"
            disabled={!!actionLoading || containerStatus === 'not_found' || containerStatus === null}
            onClick={() => handleContainerAction('start')}
          >
            {actionLoading === 'start' ? (
              <><Spinner size="sm" animation="border" className="me-1" />Starte...</>
            ) : '▶ Starten'}
          </Button>
        )}
      </div>
    </Card>
  );
}

export default StreamingSidebar;